import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

import LazyImage from '../../../components/LazyImage';

const LeaderboardCard = ({ leaderboard, currentUserId }) => {
  const getRankIcon = (rank) => {
    if (rank === 1) return { icon: 'Trophy', color: 'text-warning', bg: 'bg-warning/10' };
    if (rank === 2) return { icon: 'Medal', color: 'text-muted-foreground', bg: 'bg-muted/30' };
    if (rank === 3) return { icon: 'Award', color: 'text-warning/70', bg: 'bg-warning/5' };
    return null;
  };

  if (!leaderboard || leaderboard?.length === 0) {
    return (
      <div className="text-center py-12 bg-muted/30 rounded-xl">
        <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No friends yet. Connect with others to see the leaderboard!</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border bg-primary/5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Top Performers</h3>
            <p className="text-sm text-muted-foreground">Ranked by total wellness points</p>
          </div>
          <Icon name="TrendingUp" size={24} className="text-primary" />
        </div>
      </div>
      {/* Leaderboard List */}
      <div className="divide-y divide-border">
        {leaderboard?.map((user, index) => {
          const rank = index + 1;
          const rankConfig = getRankIcon(rank);
          const isCurrentUser = user?.id === currentUserId;

          return (
            <motion.div
              key={user?.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`p-4 hover:bg-muted/30 transition-colors duration-200 ${
                isCurrentUser ? 'bg-primary/5 border-l-4 border-l-primary' : ''
              }`}
            >
              <div className="flex items-center space-x-4">
                {/* Rank */}
                <div className="flex-shrink-0 w-12 text-center">
                  {rankConfig ? (
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-full ${rankConfig?.bg}`}>
                      <Icon name={rankConfig?.icon} size={20} className={rankConfig?.color} />
                    </div>
                  ) : (
                    <span className="text-2xl font-bold text-muted-foreground">#{rank}</span>
                  )}
                </div>

                {/* Avatar */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    {user?.avatar_url ? (
                      <LazyImage
                        src={user?.avatar_url}
                        alt={user?.full_name || 'User avatar'}
                        className="w-12 h-12 rounded-full"
                        sizes="48px"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center border-2 border-border">
                        <span className="text-lg font-semibold text-primary">
                          {user?.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    {isCurrentUser && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center border-2 border-card">
                        <Icon name="User" size={10} color="white" />
                      </div>
                    )}
                  </div>
                </div>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <h4 className="text-sm font-semibold text-foreground truncate">
                      {user?.full_name}
                      {isCurrentUser && <span className="text-primary ml-1">(You)</span>}
                    </h4>
                  </div>
                  <div className="flex items-center space-x-3 mt-1">
                    <div className="flex items-center space-x-1">
                      <Icon name="Zap" size={14} className="text-warning" />
                      <span className="text-xs text-muted-foreground">
                        {user?.current_streak || 0} day streak
                      </span>
                    </div>
                    {(user?.duels_won > 0 || user?.duels_completed > 0) && (
                      <div className="flex items-center space-x-1">
                        <Icon name="Swords" size={12} className="text-primary" />
                        <span className="text-xs text-muted-foreground">
                          {user?.duels_won || 0}W
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Points */}
                <div className="flex-shrink-0 text-right">
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} className="text-warning" />
                    <span className="text-lg font-bold text-foreground">
                      {user?.total_points?.toLocaleString() || 0}
                    </span>
                  </div>
                  <span className="text-xs text-muted-foreground">points</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      {/* Footer */}
      {leaderboard?.length >= 10 && (
        <div className="p-4 bg-muted/30 text-center">
          <p className="text-sm text-muted-foreground">Showing top 10 performers</p>
        </div>
      )}
    </div>
  );
};

export default LeaderboardCard;